<template>
  <GenericModal ref="modal" :name="content?.callback_title" css-class="inquiry-modal">
    <ModalLoader v-if="isLoading" />
    <template v-else-if="content?.form_builder_activated">
      <FormBuilderSection
        :is-in-popin="true"
        :blok="type === 'callback' ? content?.request_a_call[0] : content?.request_a_visit[0]"
        :facility="facility"
        :inject-values="refInjectValues"
      />
    </template>
    <template v-else>
      <template v-if="!isSent">
        <Form v-show="!sendError" v-slot="{ errors }" @submit="handleSubmit">
          <div class="header">
            <template v-if="type === 'callback'">
              <h2 class="size-xl">{{ content?.callback_title }}</h2>
              <p class="size-xs">{{ content?.callback_description }}</p>
            </template>
            <template v-else>
              <h2 class="size-xl">{{ content?.visit_title }}</h2>
              <p class="size-xs">
                {{
                  useStringParams(content?.visit_description, {
                    facilityName: facility?.commercialName,
                  })
                }}
              </p>
            </template>
          </div>

          <div class="inquiry-information-section">
            <p class="size-2xs">{{ content?.required_fields_mention }}</p>
            <div class="fields-list">
              <template v-if="type === 'callback'">
                <FormSelect
                  v-if="content?.callback_purposes?.length"
                  :id="prefixId('callback-purpose')"
                  name="request"
                  :label="content?.callback_purpose_select_label"
                  :options="content.callback_purposes.map((purpose) => purpose.label)"
                  required
                />

                <FormSelect
                  v-if="content?.callback_timeslots?.length"
                  :id="prefixId('callback-timeslot')"
                  name="timeSlot"
                  :label="content?.callback_timeslot_select_label"
                  :options="
                    content.callback_timeslots.map((timeslot) => timeslot.label)
                  "
                  required
                />
              </template>
              <template v-else>
                <FormSelect
                  v-if="content?.visit_timeslots?.length"
                  :id="prefixId('visit-timeslot')"
                  name="preferredDay"
                  :label="content?.visit_timeslot_select_label"
                  :options="content.visit_timeslots.map((timeslot) => timeslot.label)"
                  required
                />
              </template>
            </div>
          </div>

          <div class="user-information-section">
            <div class="user-information-header">
              <h2 class="size-m">{{ content?.user_information_title }}</h2>
              <p class="user-information-description size-2xs">
                {{ content?.user_information_description }}
              </p>
            </div>
            <div class="fields-list flex-2-per-row">
              <FormInput
                :id="prefixId('firstname')"
                name="firstName"
                :label="content?.user_firstname_input_label"
                type="text"
                :max-length="limitInputs ?? '50'"
                rules="required|alpha"
                required
              />
              <FormInput
                :id="prefixId('lastname')"
                name="lastName"
                :label="content?.user_lastname_input_label"
                type="text"
                :max-length="limitInputs ?? '50'"
                rules="required|alpha"
                required
              />
              <FormInput
                :id="prefixId('phone')"
                name="phone"
                :label="content?.user_phone_input_label"
                type="tel"
                max-length="15"
                required
              />
              <FormInput
                :id="prefixId('email')"
                name="email"
                :label="content?.user_mail_input_label"
                type="email"
                required
              />
              <FormTextarea
                v-if="type === 'visit'"
                :id="prefixId('more-info')"
                name="message"
                :label="content?.visit_more_info_input_label"
                rows="4"
              />
            </div>
          </div>

          <div class="terms-and-conditions-section size-2xs">
            <div
              v-if="privacyExplanation?.length"
              v-html="privacyExplanation"
            />
            <FormCheckbox
              :id="prefixId('terms-and-conditions')"
              name="cguContent"
              :label="termsAndConditionsLabel"
              required
            />
          </div>

          <FormCaptcha
            v-if="turnstileSitekey && modal.isOpen"
            ref="captcha"
            class="captcha"
          />

          <SimpleButton
            class="send-button"
            solid
            xl
            :disabled="isSending || !!errors['captchaToken']"
          >
            {{ content?.send_button_label }}
          </SimpleButton>
        </Form>
        <FormResponse
          v-if="sendError"
          :title="sbError?.title || sendError.message"
          :description="sbError?.description"
          :cta-label="content?.error_cta_label"
          errored
          @click="backToForm"
        />
      </template>
      <FormResponse
        v-else
        :title="content?.confirmation_title"
        :description="content?.confirmation_description"
        :cta-label="content?.confirmation_cta_label"
        @click="closeModal"
      />
    </template>
  </GenericModal>
</template>

<script setup>
import { Form } from "vee-validate";
import countries from "i18n-iso-countries";
import { useSnakeCaseFilter } from "@/composables/SnakecaseFilter";
import { useTrackingStore } from "@/stores/tracking";

const trackingStores = useTrackingStore();
const datasourcesStore = useDatasourcesStore();

const props = defineProps({
  type: {
    type: String,
    required: false,
    default: "callback",
    validator(value) {
      return ["callback", "visit"].includes(value);
    },
  },
  facility: {
    type: Object,
    required: false,
    default: () => {},
  },
});
const limitInputs = datasourcesStore.getLimitInputField;

const modal = ref(null);
const isSent = ref(false);

const captcha = ref(null);
const refInjectValues = ref(null);

const closeModal = () => {
  modal.value && modal.value.closeModal();
  tracking({
    event: "click.navigation",
    click: "close",
    click_chapter1: "popin",
    form_name: getFormName(),
    form_type: "popin_form",
  });
};
const openModal = (injectValues) => {
  isSent.value = false;
  modal.value && modal.value.openModal();
  if (injectValues) refInjectValues.value = injectValues;
  nextTick(() => {
    tracking({
      event: "popin.display",
      form_name: getFormName(),
      form_type: "popin_form",
    });
  });
};
defineExpose({
  openModal,
  closeModal,
});

const config = useRuntimeConfig();
const turnstileSitekey = config.public["turnstileSitekey"];
const isLoading = ref(true);
const story = ref(null);

onMounted(async () => {
  try {
    story.value = await useAsyncStoryblok(
      config.public["storyblokCountryPrefix"] + "/forms",
      {
        version: config.public["storyblokVersion"],
      }
    );
    isLoading.value = false;
  } catch (error) {
    console.error(error);
    isLoading.value = false;
  }
});

const content = computed(() => story.value?.value?.content);

const privacyExplanation = computed(() => {
  const value = content.value?.privacy_explanation;
  return value ? useRenderRichText(value) : null;
});
const termsAndConditionsLabel = computed(() => {
  const value = content.value?.terms_and_conditions_label;
  return value ? useRenderRichText(value) : null;
});

const query = gql`
  mutation createLead($leadData: LeadInput!) {
    firstLead: insertLead(leadData: $leadData) {
      uuid
      locale
      tracking
      formType
      facilityUniqueKey
      firstName
      lastName
      email
      phone
      timeSlot
      preferredDay
      request
      cguContent
      createdAt
      updatedAt
      message
    }
  }
`;

const {
  mutate: sendData,
  error: sendError,
  onDone: onSendSuccess,
  onError: onSendError,
  loading: isSending,
} = useMutation(query);

const tracking = (data) => {
    trackingStores.sendTrackingData(data);
};

const getFormName = () => getInquiryFormName(props.type);

const handleSubmit = (values) => {
  const formType =
    props.type === "visit"
      ? "REQUEST_VISIT"
      : props.facility?.facilityUniqueKey
      ? "REQUEST_CALL_FACILITY"
      : "REQUEST_CALL";

  const locale = countries
    .getName(config.public["country"], "en")
    .toUpperCase();

  const variables = {
    leadData: {
      ...values,
      locale,
      formType,
      facilityUniqueKey: props.facility?.facilityUniqueKey,
      tracking: window.location.href,
    },
  };
  sendData(variables);
  captcha.value?.reset();
};

onSendSuccess(() => {
  isSent.value = true;
  tracking({
    event: "click.action",
    click: useSnakeCaseFilter(content.value?.send_button_label),
    click_chapter1: "popin",
    click_chapter2: "CTA",
    form_name: getFormName(),
    form_type: "popin",
  });
});

onSendError((error) => {
  console.error(error);
});

const sbError = computed(() => {
  return content.value?.errors?.find((error) => error.id === sendError.value?.message);
});

const backToForm = () => {
  isSent.value = false;
  sendError.value = null;
};

const uuid = useId();

const prefixId = (value) => {
  return `${uuid}-${value}`;
};
</script>

<style lang="scss">
.inquiry-modal.modal {
  z-index: $z-index-modal + 1;
}
</style>

<style lang="scss" scoped>
form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 3rem;

  @include for-tablet-landscape-up {
    gap: 4rem;
  }

  div {
    z-index: initial;
  }
}

.header,
.fields-list,
.user-information-section,
.user-information-header,
.inquiry-information-section,
.terms-and-conditions-section {
  display: flex;
  flex-direction: column;
}
.header,
.user-information-header {
  gap: 0.5rem;
  text-align: center;
}
.header {
  @include for-tablet-landscape-up {
    gap: 1rem;
  }
}
.fields-list {
  $field-gap: 0.625rem;
  gap: $field-gap;

  &.flex-2-per-row {
    @include for-tablet-landscape-up {
      flex-direction: row;
      flex-wrap: wrap;

      > * {
        flex: calc(50% - $field-gap);
      }
    }
  }
}

.user-information-section {
  gap: 1.5rem;
  margin-top: -0.725rem;

  @include for-tablet-landscape-up {
    gap: 2.5rem;
  }
}
.inquiry-information-section {
  gap: 1rem;

  @include for-tablet-landscape-up {
    gap: 1.5rem;
  }
}
.terms-and-conditions-section {
  gap: 0.75rem;
}
.send-button {
  margin: 0 auto;
}
.user-information-description {
  color: var(--text-70);
}
.captcha {
  margin-top: -3rem;

  @include for-tablet-landscape-up {
    margin-top: -4rem;
  }
}
:deep(.form-builder-item .description) {
  margin-top: 2rem;

  &:has(+ .form-builder-group) {
    text-align: center;

    h2 {
      margin-bottom: 1rem;
    }
  }
}
</style>
